import { useState } from 'react'
import { toast } from 'sonner'
import { numeDepartament, type DepartamentSlug } from '@b4l/shared'
import {
  BlocResponsabili,
  ComutatorDestinatie,
  useResponsabili,
  type ModDestinatie,
} from './DestinatieTask'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { X } from 'lucide-react'
import { api } from '../../lib/api'
import {
  AlegeDepartamente,
  listaDepartamente,
  trimiteLa,
} from '../../components/taskuri/AlegeDepartamente'
import {
  aziIso,
  mesajEroare,
  useCere,
  useCreeaza,
  useReimprospateaza,
  useTrimite,
  type CardTask,
} from './date'

/** Aceeași limită ca pe server (`MAX_MARIME`, `lib/taskAttachments.ts`): refuzul se vede înainte. */
const MAX_DOCUMENT = 20 * 1024 * 1024

const camp =
  'w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text placeholder:text-b4l-text-dim'
const eticheta = 'text-mic font-semibold text-b4l-text-muted'

/**
 * „Task nou" (macheta 19.10.2026: https://claude.ai/artifact/E5onnbcP1QwbbeUVdDXKYG). Pentru
 * oricine, doua feluri:
 *
 *   - DEPARTAMENTE (unul sau mai multe): intra la „De preluat", il preia cine are timp. Trimis
 *     altui departament devine cerere: il vedem la „Trimise de noi";
 *   - RESPONSABILI (unul sau mai multi, si tu): direct „În lucru" la ei. Il vede si departamentul
 *     ales — sau, bifat „Privat", doar ei si tu, fara departament.
 *
 * Termenul e optional — doar cand exista o data limita reala. Cu `pentruTask`, acelasi formular e
 * „Cer altui departament": task-ul nostru trece in „Așteaptă" si revine singur cand ei il termina.
 */
export function FormularTask({
  alMeu,
  departamentInitial,
  privatInitial = false,
  titluInitial = '',
  descriereInitiala = '',
  pentruTask,
  dinMail,
  laInchidere,
  laCreat,
}: {
  alMeu: DepartamentSlug | null
  departamentInitial: DepartamentSlug | null
  /** Deschis din „Ale mele": privat, pentru tine. */
  privatInitial?: boolean
  /** Deschis din mail: subiectul și textul mailului, de corectat înainte de trimitere. */
  titluInitial?: string
  descriereInitiala?: string
  pentruTask?: CardTask | { id: number; titlu: string; departament: DepartamentSlug | null }
  /** Mailul din care se face task-ul (27.10.2026): același mail nu mai face al doilea task. */
  dinMail?: { messageId: string; subiect: string; expeditor: string }
  laInchidere: () => void
  laCreat?: (id: number) => void
}) {
  const [alese, setAlese] = useState<DepartamentSlug[]>(
    departamentInitial ? [departamentInitial] : [],
  )
  // Un singur departament: textele de pana acum. Mai multe: cate un task la fiecare.
  const dep = alese.length === 1 ? alese[0]! : null
  const maiMulte = alese.length > 1
  // Cui dai task-ul: departamentelor sau unor responsabili — vezi `DestinatieTask`.
  const [mod, setMod] = useState<ModDestinatie>(
    privatInitial && !pentruTask ? 'responsabili' : 'departamente',
  )
  const resp = useResponsabili(alMeu, privatInitial && !pentruTask)
  const peResponsabili = mod === 'responsabili' && !pentruTask
  const [titlu, setTitlu] = useState(titluInitial.slice(0, 200))
  const [descriere, setDescriere] = useState(descriereInitiala)
  const [cuTermen, setCuTermen] = useState(false)
  const [termen, setTermen] = useState('')
  const [urgent, setUrgent] = useState(false)
  const [legat, setLegat] = useState('')
  /*
    Documentele, din formular (Madalin, 16.10.2026): nu mai creezi task-ul și apoi îl deschizi ca
    să atașezi. Se încarcă imediat după creare, pe ruta obișnuită a documentelor — aceleași
    verificări —, unul după altul.
  */
  const [fisiere, setFisiere] = useState<File[]>([])
  const [seIncarca, setSeIncarca] = useState(false)
  const reimprospateaza = useReimprospateaza()
  const creeaza = useCreeaza()
  const cere = useCere()
  const seTrimite = creeaza.isPending || cere.isPending || seIncarca

  const adaugaFisiere = (noi: File[]) => {
    const prea = noi.filter((f) => f.size > MAX_DOCUMENT)
    if (prea.length)
      toast.error(`Peste 20 MB, nu se poate atașa: ${prea.map((f) => f.name).join(', ')}`)
    setFisiere((v) => [...v, ...noi.filter((f) => f.size <= MAX_DOCUMENT)])
  }

  /**
   * După creare: task-ul există deja, deci un document căzut nu-l pierde — se spune care.
   * `laGrup`: trimis la mai multe departamente — o încărcare, serverul îl pune la toate.
   */
  const incarcaPe = async (taskId: number, laGrup = false) => {
    if (!fisiere.length) return
    setSeIncarca(true)
    const cazute: string[] = []
    for (const f of fisiere) {
      const fd = new FormData()
      fd.append('file', f)
      try {
        await api.post(`/tasks/${taskId}/atasamente${laGrup ? '?laGrup=1' : ''}`, fd, {
          timeout: 120_000,
        })
      } catch (e) {
        cazute.push(`${f.name} (${mesajEroare(e, 'eroare')})`)
      }
    }
    setSeIncarca(false)
    reimprospateaza()
    if (cazute.length) {
      toast.error(
        `Task-ul s-a creat, dar ${cazute.length === 1 ? 'un document nu s-a încărcat' : `${cazute.length} documente nu s-au încărcat`}: ${cazute.join('; ')}. Le atașezi din task.`,
      )
    }
  }

  const cerere = !!pentruTask
  // Cerere = la alt departament decat al nostru (sau decat al task-ului, la „Cer altui departament").
  const laAltii = !!dep && (cerere ? dep !== pentruTask.departament : !!alMeu && dep !== alMeu)
  const titluFereastra = cerere
    ? 'Cer altui departament'
    : maiMulte
      ? 'Task nou'
      : laAltii && dep
        ? `Cerere către ${numeDepartament(dep)}`
        : 'Task nou'

  const trimite = () => {
    if (peResponsabili) {
      if (resp.lipsa || !titlu.trim()) return
      creeaza.mutate(
        {
          ...resp.corp(),
          titlu: titlu.trim(),
          descriere: descriere.trim() || null,
          termen: cuTermen && termen ? termen : null,
          urgent,
          clientLabel: legat.trim() || null,
          dinMail,
        },
        {
          onSuccess: async (r) => {
            await incarcaPe(r.task.id)
            laInchidere()
            laCreat?.(r.task.id)
          },
        },
      )
      return
    }
    if (alese.length === 0 || !titlu.trim()) return
    const comun = {
      departamente: alese,
      titlu: titlu.trim(),
      descriere: descriere.trim() || null,
      termen: cuTermen && termen ? termen : null,
      urgent,
    }
    if (pentruTask) {
      cere.mutate(
        { id: pentruTask.id, ...comun },
        {
          onSuccess: async (r: { cerere?: { id: number } }) => {
            if (r.cerere?.id) await incarcaPe(r.cerere.id, maiMulte)
            if (maiMulte) toast.success(`Cerere trimisă la ${listaDepartamente(alese)}.`)
            laInchidere()
          },
        },
      )
    } else {
      creeaza.mutate(
        { ...comun, clientLabel: legat.trim() || null, dinMail },
        {
          onSuccess: async (r) => {
            await incarcaPe(r.task.id, maiMulte)
            if (maiMulte) toast.success(`Trimis la ${listaDepartamente(alese)}.`)
            laInchidere()
            laCreat?.(r.task.id)
          },
        },
      )
    }
  }

  return (
    <Modal
      open
      onClose={laInchidere}
      title={titluFereastra}
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          {peResponsabili ? (
            <Button onClick={trimite} disabled={!!resp.lipsa || !titlu.trim()} loading={seTrimite}>
              {resp.textButon}
            </Button>
          ) : (
            <Button onClick={trimite} disabled={!alese.length || !titlu.trim()} loading={seTrimite}>
              {trimiteLa(alese)}
            </Button>
          )}
        </>
      }
    >
      <form
        className="grid gap-3.5"
        onSubmit={(e) => {
          e.preventDefault()
          trimite()
        }}
      >
        {pentruTask && (
          <p className="rounded-caseta bg-b4l-bg-soft px-3 py-2 text-mic text-b4l-text-muted">
            Pentru „{pentruTask.titlu}”. Task-ul tău trece în „Așteaptă” și revine singur în „În
            lucru” când ei îl termină.
          </p>
        )}
        {!pentruTask && <ComutatorDestinatie mod={mod} setMod={setMod} />}
        {peResponsabili ? (
          <BlocResponsabili r={resp} alMeu={alMeu} />
        ) : (
          <div className="grid gap-1.5">
            <span className={eticheta}>Departamente</span>
            <AlegeDepartamente
              alese={alese}
              setAlese={setAlese}
              alMeu={alMeu}
              exclus={pentruTask ? pentruTask.departament : null}
            />
            {maiMulte ? (
              <div className="grid gap-1 rounded-caseta bg-b4l-bg-soft px-3 py-2 text-mic text-b4l-text">
                {cerere ? (
                  <>
                    <span>
                      Pleacă câte o cerere la <b>{listaDepartamente(alese)}</b>.
                    </span>
                    <span className="text-b4l-text-muted">
                      Task-ul tău revine din „Așteaptă” abia când au terminat toate. Până atunci
                      scrie pe el pe cine mai aștepți.
                    </span>
                  </>
                ) : (
                  <>
                    <span>
                      Pleacă <b>{alese.length} task-uri</b>, câte unul la{' '}
                      <b>{listaDepartamente(alese)}</b>. Intră la „De preluat” în fiecare.
                    </span>
                    <span className="text-b4l-text-muted">
                      Fiecare departament îl preia și îl închide separat.
                      {fisiere.length > 0 && ' Documentele atașate ajung la toate.'} Primești o
                      singură notificare, când au terminat toate.
                    </span>
                  </>
                )}
              </div>
            ) : dep ? (
              <div className="grid gap-1 rounded-caseta bg-b4l-bg-soft px-3 py-2 text-mic text-b4l-text">
                {laAltii ? (
                  <>
                    <span>
                      Ajunge la <b>{numeDepartament(dep)}</b>, în coloana „De preluat”. Îl ia cine
                      are timp de acolo.
                    </span>
                    <span className="text-b4l-text-muted">
                      Îl vezi la „Trimise de noi” și primești notificare când e gata.
                    </span>
                  </>
                ) : (
                  <span>
                    Intră la <b>„De preluat”</b> în {numeDepartament(dep)}. Îl ia cine are timp —
                    inclusiv tu, cu „Preia”.
                  </span>
                )}
              </div>
            ) : (
              <span className="text-mic text-b4l-text-dim">Alege unde merge task-ul.</span>
            )}
          </div>
        )}
        <label className="grid gap-1.5">
          <span className={eticheta}>Ce trebuie făcut</span>
          <input
            autoFocus
            className={camp}
            maxLength={200}
            value={titlu}
            onChange={(e) => setTitlu(e.target.value)}
            placeholder="ex: 30 de kituri de mostre pentru vizita la Plafar"
          />
        </label>
        <label className="grid gap-1.5">
          <span className={eticheta}>
            Detalii <span className="font-normal text-b4l-text-dim">(opțional)</span>
          </span>
          <textarea
            rows={3}
            className={camp}
            maxLength={5000}
            value={descriere}
            onChange={(e) => setDescriere(e.target.value)}
            placeholder="Context, cantități, linkuri. Documentele le atașezi mai jos."
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid content-start gap-1.5">
            <span className={eticheta}>Termen</span>
            {cuTermen ? (
              <input
                type="date"
                className={camp}
                min={aziIso()}
                value={termen}
                onChange={(e) => setTermen(e.target.value)}
                aria-label="Termen"
                autoFocus
              />
            ) : (
              <button
                type="button"
                onClick={() => setCuTermen(true)}
                className="rounded-caseta border border-dashed border-b4l-border px-2.5 py-2 text-left text-corp text-b4l-text-muted hover:border-b4l-blue hover:text-b4l-blue-dark"
              >
                + Are o dată limită reală
              </button>
            )}
          </div>
          <div className="grid content-start gap-1.5">
            <span className={eticheta}>Prioritate</span>
            <label className="inline-flex cursor-pointer items-center gap-2 py-2 text-corp text-b4l-text">
              <input
                type="checkbox"
                checked={urgent}
                onChange={(e) => setUrgent(e.target.checked)}
                className="h-4 w-4 accent-b4l-red"
              />
              Urgent <span className="text-b4l-text-dim">— apare cu bară roșie</span>
            </label>
          </div>
        </div>
        {!pentruTask && (
          <label className="grid gap-1.5">
            <span className={eticheta}>
              Legat de <span className="font-normal text-b4l-text-dim">(opțional)</span>
            </span>
            <input
              className={camp}
              maxLength={120}
              value={legat}
              onChange={(e) => setLegat(e.target.value)}
              placeholder="client, comandă, campanie, factură…"
            />
          </label>
        )}
        <div className="grid gap-1.5">
          <span className={eticheta}>
            Documente <span className="font-normal text-b4l-text-dim">(opțional)</span>
          </span>
          {fisiere.length > 0 && (
            <ul className="grid gap-1">
              {fisiere.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-2 rounded-caseta bg-b4l-bg-soft px-2.5 py-1.5 text-corp"
                >
                  <span className="min-w-0 flex-1 truncate text-b4l-text">{f.name}</span>
                  <span className="shrink-0 text-mic tabular-nums text-b4l-text-dim">
                    {f.size < 1024 * 1024
                      ? `${Math.max(1, Math.round(f.size / 1024))} KB`
                      : `${(f.size / 1024 / 1024).toFixed(1)} MB`}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFisiere((v) => v.filter((_, j) => j !== i))}
                    disabled={seTrimite}
                    className="rounded-md p-0.5 text-b4l-text-dim hover:bg-b4l-bg hover:text-b4l-red-dark"
                    aria-label={`Scoate ${f.name}`}
                    title="Scoate"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <label className="cursor-pointer justify-self-start text-corp text-b4l-blue hover:underline">
            {seIncarca ? 'Se încarcă documentele…' : '+ Atașează documente'}
            <input
              type="file"
              multiple
              className="hidden"
              disabled={seTrimite}
              onChange={(e) => {
                const f = Array.from(e.target.files ?? [])
                e.target.value = ''
                if (f.length) adaugaFisiere(f)
              }}
            />
          </label>
        </div>
        {/* Enter in campul de titlu trimite formularul. */}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}

export function ModalTrimite({
  task,
  laInchidere,
  laTrimis,
}: {
  task: { id: number; titlu: string; departament: DepartamentSlug | null }
  laInchidere: () => void
  /** Dupa trimitere task-ul pleaca din departament — cine l-a deschis de obicei nu-l mai vede. */
  laTrimis?: () => void
}) {
  const [alese, setAlese] = useState<DepartamentSlug[]>([])
  const [motiv, setMotiv] = useState('')
  const trimite = useTrimite()
  const [prim, ...restul] = alese
  return (
    <Modal
      open
      onClose={laInchidere}
      title="Nu e al nostru — trimite altui departament"
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          <Button
            disabled={!alese.length}
            loading={trimite.isPending}
            onClick={() =>
              alese.length &&
              trimite.mutate(
                { id: task.id, departamente: alese, motiv: motiv.trim() || null },
                {
                  onSuccess: () => {
                    toast.success(`Trimis la ${listaDepartamente(alese)}.`)
                    laInchidere()
                    laTrimis?.()
                  },
                },
              )
            }
          >
            {trimiteLa(alese)}
          </Button>
        </>
      }
    >
      <div className="grid gap-3.5">
        <p className="text-mic text-b4l-text-muted">„{task.titlu}”</p>
        <div className="grid gap-1.5">
          <span className={eticheta}>La ce departament ajunge</span>
          <AlegeDepartamente alese={alese} setAlese={setAlese} exclus={task.departament} />
          {restul.length ? (
            <span className="rounded-caseta bg-b4l-bg-soft px-3 py-2 text-mic text-b4l-text">
              Task-ul pleacă la <b>{numeDepartament(prim!)}</b>, iar{' '}
              <b>{listaDepartamente(restul)}</b>{' '}
              {restul.length === 1 ? 'primește o copie' : 'primesc câte o copie'}, cu documentele.
              Intră la „De preluat” în fiecare; mutarea rămâne scrisă în traseu.
            </span>
          ) : (
            <span className="text-mic text-b4l-text-dim">
              Intră la „De preluat” acolo. Mutarea rămâne scrisă în traseu.
            </span>
          )}
        </div>
        <label className="grid gap-1.5">
          <span className={eticheta}>
            De ce <span className="font-normal text-b4l-text-dim">(opțional)</span>
          </span>
          <input
            className={camp}
            maxLength={500}
            value={motiv}
            onChange={(e) => setMotiv(e.target.value)}
          />
        </label>
      </div>
    </Modal>
  )
}
